/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { Microphone } from '@carbon/react/icons';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import { useAppSelector } from '../../../../../store';
import {
  selectIsElevenlabsEnabled,
  selectIsGenesysAudioConnectorInCpd
} from '../../../../ia/store/features/selectors';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import {
  selectPhoneIntegrationType,
  selectTTSInstanceCreateRequired
} from '../../../store/phone/selectors';
import { GENESYS_AUDIO_CONNECTOR_TYPE } from '../../../util/channelTypes';
import { ChannelContentProps } from '../../../util/types';
import { TabContent } from '../../common/tabbed-header/TabContent';
import { TTS_PROVIDER_ELEVENLABS_ID } from '../constants';
import { FIELDS } from '../schema';
import { AudioVisualizer } from './AudioVisualizer';
import { AudioVisualizerElevenLabs } from './AudioVisualizerElevenLabs';
import { TextToSpeech as messages } from './messages';
import { TextToSpeech } from './TextToSpeech';
import './TextToSpeech.scss';
import { TextToSpeechCpd } from './TextToSpeechCpd';

export const TextToSpeechTab = ({ readOnly }: ChannelContentProps) => {
  const formMethods = useFormContext();
  const { getFieldValue } = useFormHelpers(formMethods);

  const TTSInstanceCreateRequired = useAppSelector(
    selectTTSInstanceCreateRequired
  );
  const isGenesysAudioConnectorEnabledInCpd = useAppSelector(
    selectIsGenesysAudioConnectorInCpd
  );
  const contactCenterProvider = useAppSelector(selectPhoneIntegrationType);
  const is11labsEnabled = useAppSelector(selectIsElevenlabsEnabled);

  // Below will give which contact center aka connections type is selected
  const isGenesysAudioConnectorSelected =
    contactCenterProvider === GENESYS_AUDIO_CONNECTOR_TYPE;
  // As of writing this there are only two type of TTS Providers watson tts and 11labs
  const provider = getFieldValue(FIELDS.TTS.PROVIDER);

  return (
    <TabContent
      ImageComponent={() => <Microphone size={32} />}
      title={<FormattedMessage {...messages.title} />}>
      <div className="TextToSpeech__body">
        <div className="TabContent__subtitle">
          <FormattedMessage {...messages.stepDescription} />
          <div className="TextToSpeech__note">
            <FormattedMessage {...messages.stepDescriptionNote} />
          </div>
        </div>
        <div className="TabContent__spacer32" />
        {isGenesysAudioConnectorEnabledInCpd ? (
          <TextToSpeechCpd />
        ) : (
          <TextToSpeech readOnly={readOnly} />
        )}
      </div>
      {is11labsEnabled &&
        isGenesysAudioConnectorSelected &&
        provider === TTS_PROVIDER_ELEVENLABS_ID && (
          <AudioVisualizerElevenLabs />
        )}
      {!TTSInstanceCreateRequired &&
        !isGenesysAudioConnectorEnabledInCpd &&
        provider !== TTS_PROVIDER_ELEVENLABS_ID && <AudioVisualizer />}
    </TabContent>
  );
};
